/**
 * Plan 58: defence-in-depth normalizer for finding severity tiers.
 *
 * Mirrors the backend's
 * services/negotiation/clause_analysis_agent.py
 * normalize_negotiation_severity() so the UI tolerates pre-Plan-58
 * negotiation_findings rows that still have risk_level='critical' (folded
 * to 'high' by the data migration in Plan 58 Task 7, but the UI may ship
 * before that migration runs in any given environment).
 *
 * Non-string / unknown values silently default to 'medium' — matches the
 * backend helper's silent-default behaviour, so the UI never throws on
 * malformed data.
 */

export type Severity = 'high' | 'medium' | 'low';

const VALID = new Set<Severity>(['high', 'medium', 'low']);

const FALLBACK: Record<string, Severity> = {
  critical: 'high',
  severe: 'high',
  major: 'high',
  minor: 'low',
  informational: 'low',
  info: 'low',
  none: 'low',
};

export function normalizeSeverity(raw: unknown): Severity {
  if (typeof raw !== 'string') return 'medium';
  const lowered = raw.trim().toLowerCase();
  if ((VALID as Set<string>).has(lowered)) return lowered as Severity;
  return FALLBACK[lowered] ?? 'medium';
}
